import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { OverlayConfig } from '@zipari/shared-ds-util-overlay';
import { WindowService } from '../../shared/services/window.service';
import { checkInputsForText } from '../../design-system.helper';

@Component({
  selector: 'mp-overlay',
  styleUrls: ['./overlay.component.scss'],
  templateUrl: './overlay.component.html',
})
/**
 * @class OverlayComponent
 * @classdesc Component for displaying script-injected content over current view
 * @property {string} containerId is the container id to be supplied to the vendor for an insertion point
 * @property {string} dynamicClass controls the way the content is displayed on the page the view will default to `overlay`
 * @property {string} primeId a unique identifier for the logged in user
 */
export class OverlayComponent implements OnChanges, OnInit {
  @Input() config: OverlayConfig;
  containerId: string;
  dynamicClass: string;
  primeId: string;

  constructor(private windowService: WindowService) {}

  ngOnInit(): void {
    const [config] = checkInputsForText([this.config]);

    this.config = config;
  }

  /** Allow passthrough of vendor-requested content, set global parameter, and execute related code. */
  ngOnChanges(): void {
    if (!this.config) return;
    const { id, style, primeId, vendorParams } = this.config;

    this.dynamicClass = style ? `Overlay__${style.toLowerCase()}` : '';
    this.containerId = id || 'overlay-id-temp';
    this.primeId = primeId || null;

    if (!vendorParams) return;

    const { windowVarName, externalVarName, executionCommand } = vendorParams;
    const customValue = { [externalVarName]: this.config.primeId || '' };

    windowVarName.includes('.')
      ? this.handleNestedProperty(windowVarName, customValue)
      : this.handleSimpleProperty(windowVarName, customValue);

    // Trigger code programmatically if configured
    if (this.someExecutionCommand(executionCommand)) {
      executionCommand.includes('.')
        ? this.handleNestedMethod(executionCommand)
        : this.handleSimpleMethod(executionCommand);
    }
  }
  someExecutionCommand = (executionCommand): boolean =>
    0 < executionCommand?.length;

  /** Handle nested property eg: window.property1.property2.property3 */
  handleNestedProperty(windowVarName: string, customValue: unknown): void {
    const varArray = windowVarName.split('.');
    let windowReference = this.windowService.nativeWindow;

    for (let v = 0; v < varArray.length; v++) {
      const customGlobalParameter = windowReference;

      customGlobalParameter[varArray[v]] = windowReference[varArray[v]] || {};
      if (this.lastVar(v, varArray)) {
        customGlobalParameter[varArray[v]] = customValue;
      }
      windowReference = customGlobalParameter[varArray[v]];
    }
  }
  lastVar = (v: number, varArray: string[]): boolean =>
    v === varArray.length - 1;

  /** Handle simple property eg: window.property */
  handleSimpleProperty(windowVarName: string, customValue: unknown): void {
    if (this.undefinedWindowVarName(windowVarName)) {
      window[windowVarName] = {} as Window;
    }
    window[windowVarName] = customValue;
  }
  undefinedWindowVarName = (windowVarName: string): boolean =>
    typeof window[windowVarName] === 'undefined';

  /** Handle nested method eg: window.property1.property2.method() */
  handleNestedMethod(executionCommand): void {
    const propArray = executionCommand.split('.');
    let windowRef = this.windowService.nativeWindow;

    for (let v = 0; v < propArray.length; v++) {
      windowRef = windowRef[propArray[v]];

      // Execute the last property. It will be the method to execute
      if (this.lastWindowRefFunction(v, propArray, windowRef)) windowRef();
    }
  }
  lastWindowRefFunction = (
    v: number,
    propArray: string[],
    windowRef: any,
  ): boolean => v === propArray.length - 1 && typeof windowRef === 'function';

  /** Handle simple method eg: window.method() */
  handleSimpleMethod(executionCommand: string): void {
    const executable = this.windowService.nativeWindow[executionCommand];

    if (this.executableFunction(executable)) executable();
  }
  executableFunction = (executable: unknown): boolean =>
    executable && typeof executable === 'function';
}
